import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Loader2, X } from "lucide-react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useCart } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [data, setData] = useState(null);
  const { clear } = useCart();
  const { t } = useTranslation();

  useEffect(() => {
    if (!sessionId) {
      setStatus("missing");
      return;
    }
    let attempts = 0;
    const max = 8;
    const interval = 2000;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (cancelled) return;
        setData(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          clear();
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        attempts += 1;
        if (attempts >= max) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, interval);
      } catch {
        attempts += 1;
        if (attempts >= max) setStatus("error");
        else setTimeout(poll, interval);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, clear]);

  return (
    <main
      data-testid="checkout-success-page"
      className="min-h-[80vh] flex items-center justify-center pt-32 pb-24 bg-[#FBF8EF]"
    >
      <div className="max-w-md mx-auto text-center px-6">
        {status === "checking" && (
          <>
            <Loader2 size={28} className="mx-auto animate-spin text-[#5C4033]" />
            <p className="mt-6 font-serif text-2xl text-[#121212]">{t("checkout.checkingPayment")}</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="inline-flex items-center justify-center w-14 h-14 border border-[#5C4033] rounded-full mb-6">
              <Check size={22} strokeWidth={1.2} className="text-[#5C4033]" />
            </div>
            <p className="font-serif text-3xl md:text-4xl text-[#121212]">{t("checkout.paidTitle")}</p>
            <p className="mt-5 text-sm text-[#4A4641] leading-[1.85]">
              {t("checkout.paidSub")}
            </p>
            {data?.amount_total != null && (
              <p className="mt-4 text-[11px] tracking-[0.28em] uppercase text-[#5C4033]">
                {t("checkout.amount")} · {(data.amount_total / 100).toFixed(2)} {data.currency?.toUpperCase()}
              </p>
            )}
            <Link
              to="/vestiaire"
              data-testid="success-back-vestiaire"
              className="mt-12 inline-flex items-center h-12 px-8 bg-[#121212] text-[#FBF8EF] text-[11px] tracking-[0.32em] uppercase"
            >
              {t("checkout.backToVestiaire")}
            </Link>
          </>
        )}
        {(status === "timeout" || status === "error") && (
          <>
            <p className="font-serif text-2xl text-[#121212]">
              {t("checkout.pendingTitle")}
            </p>
            <p className="mt-4 text-sm text-[#4A4641]">
              {t("checkout.pendingSub")}
            </p>
            <Link
              to="/"
              className="mt-10 inline-flex items-center h-12 px-8 border border-[#121212] text-[11px] tracking-[0.32em] uppercase"
            >
              {t("checkout.backHome")}
            </Link>
          </>
        )}
        {status === "expired" && (
          <>
            <X size={26} className="mx-auto text-[#A33A2A]" />
            <p className="mt-6 font-serif text-2xl text-[#121212]">{t("checkout.expiredTitle")}</p>
            <p className="mt-4 text-sm text-[#4A4641]">
              {t("checkout.expiredSub")}
            </p>
          </>
        )}
        {status === "missing" && (
          <p className="font-serif text-xl text-[#121212]">{t("checkout.noSession")}</p>
        )}
      </div>
    </main>
  );
}
